"use client";

import { c as _c } from "react/compiler-runtime";
import type { Route } from "next";
import Link from "next/link";
import { usePathname } from "next/navigation";
import * as React from "react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { extractDistrictSlug } from "@/config/sidebar-navigation";
type SidebarBreadcrumb = {
  href: string;
  label: string;
};
const SEGMENT_LABELS: Record<string, string> = {
  applications: "Applications",
  certificates: "Certificates",
  d: "Districts",
  dashboard: "Dashboard",
  districts: "Districts",
  e: "Events",
  events: "Events",
  fixtures: "Fixtures",
  history: "History",
  me: "Me",
  new: "New",
  notifications: "Notifications",
  p: "Profiles",
  profile: "Profile",
  register: "Register",
  results: "Results",
  scores: "Scores",
  settings: "Settings",
  staff: "Staff",
  standings: "Standings",
  t: "Tournaments",
  tournaments: "Tournaments",
  v: "Venues",
  venues: "Venues"
};
const SINGULAR_SECTION_LABELS: Record<string, string> = {
  d: "District",
  districts: "District",
  e: "Event",
  events: "Event",
  p: "Profile",
  t: "Tournament",
  tournaments: "Tournament",
  v: "Venue",
  venues: "Venue"
};
const OPAQUE_SEGMENT_PATTERN = /^(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|[0-9a-f]{24,}|[A-Za-z0-9_-]{20,})$/i;
function decodeSegment(segment: string) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
function formatSegmentLabel(segment: string) {
  return decodeSegment(segment).replace(/[-_]+/g, " ").split(" ").filter(Boolean).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}
function isOpaqueSegment(segment: string) {
  return OPAQUE_SEGMENT_PATTERN.test(segment);
}
function buildSidebarBreadcrumbs(pathname: string | null): SidebarBreadcrumb[] {
  if (!pathname) {
    return [];
  }
  const districtSlug = extractDistrictSlug(pathname);
  const segments = pathname.split("/").filter(Boolean);
  const breadcrumbs: SidebarBreadcrumb[] = [];
  let href = "";
  for (const [index, segment] of segments.entries()) {
    href += `/${segment}`;

    // Inside a district workspace, collapse `/dashboard/d/<slug>` into a
    // single "Dashboard" crumb pointing at the district dashboard.
    if (districtSlug) {
      if (segment === "dashboard" && index === 0) {
        breadcrumbs.push({
          href: `/dashboard/d/${districtSlug}`,
          label: "Dashboard"
        });
        continue;
      }
      if (segment === "d" && segments[index - 1] === "dashboard") continue;
      if (segment === districtSlug && segments[index - 1] === "d") continue;
    }
    const previousSegment = segments[index - 1];
    const isLastSegment = index === segments.length - 1;
    const staticLabel = SEGMENT_LABELS[segment];
    if (staticLabel) {
      breadcrumbs.push({
        href,
        label: staticLabel
      });
      continue;
    }
    if (isOpaqueSegment(segment)) {
      if (isLastSegment && previousSegment) {
        const fallbackLabel = SINGULAR_SECTION_LABELS[previousSegment];
        if (fallbackLabel) {
          breadcrumbs.push({
            href,
            label: fallbackLabel
          });
        }
      }
      continue;
    }
    breadcrumbs.push({
      href,
      label: formatSegmentLabel(segment)
    });
  }
  return breadcrumbs;
}
export function SidebarHeaderBar() {
  const $ = _c(13);
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = buildSidebarBreadcrumbs(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const breadcrumbs = t0;
  let t1;
  if ($[2] !== breadcrumbs) {
    t1 = breadcrumbs.at(-1)?.label ?? "Workspace";
    $[2] = breadcrumbs;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const currentPageLabel = t1;
  let t2;
  if ($[4] !== currentPageLabel) {
    t2 = <h1 className="sr-only">{currentPageLabel}</h1>;
    $[4] = currentPageLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== breadcrumbs) {
    t3 = breadcrumbs.length > 0 ? breadcrumbs.map((breadcrumb, index) => {
      const isCurrentPage = index === breadcrumbs.length - 1;
      return <React.Fragment key={breadcrumb.href}><BreadcrumbItem className="min-w-0 shrink-0">{isCurrentPage ? <BreadcrumbPage className="truncate font-semibold text-foreground">{breadcrumb.label}</BreadcrumbPage> : <BreadcrumbLink asChild={true} className="truncate text-muted-foreground transition-colors hover:text-foreground"><Link href={breadcrumb.href as Route}>{breadcrumb.label}</Link></BreadcrumbLink>}</BreadcrumbItem>{!isCurrentPage ? <BreadcrumbSeparator className="shrink-0 text-muted-foreground/50 [&>svg]:size-3.5" /> : null}</React.Fragment>;
    }) : <BreadcrumbItem><BreadcrumbPage className="font-semibold text-foreground">Workspace</BreadcrumbPage></BreadcrumbItem>;
    $[6] = breadcrumbs;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <Breadcrumb><BreadcrumbList className="no-scrollbar flex-nowrap gap-1.5 overflow-x-auto whitespace-nowrap font-semibold text-base text-foreground sm:text-base lg:text-lg">{t3}</BreadcrumbList></Breadcrumb>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className="min-w-0 flex-1 pl-2">{t2}{t4}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}